/**
 * ProcurementHowToModal.jsx
 * =========================
 * First-login "How to use" guide for the Procurement — Invoice Submission portal.
 *
 * DESIGN
 *   • Plain white sheet. No image, no colour, no icons — quiet typography only,
 *     matching the monochrome portal language.
 *
 * BEHAVIOUR
 *   • Auto-opens on first mount per user (localStorage key
 *     `procurement_howto_seen:<username>`).
 *   • Reopenable via the `forceOpen` prop (driven by the "How to use" button
 *     in the dashboard header).
 *   • Closes via the primary button, the "×" in the top-right, or Escape.
 *   • "Don't show this again" checkbox (default ON).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Checkbox, Button } from 'antd';

const STORAGE_KEY_PREFIX = 'procurement_howto_seen';

const T = {
  ink: '#1f1f1f', sub: '#595959', muted: '#8c8c8c', faint: '#bfbfbf',
  border: '#d9d9d9', line: '#ececec', panel: '#fafafa', white: '#ffffff',
  blue: '#3f74ad', blueSoft: '#eaf3fc', blueLine: '#d6e6f5',
};

export default function ProcurementHowToModal({
  username = 'guest',
  forceOpen = false,
  onClose = null,
}) {
  const [visible, setVisible] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(true);

  const storageKey = `${STORAGE_KEY_PREFIX}:${username}`;

  useEffect(() => {
    try {
      const seen = localStorage.getItem(storageKey);
      if (!seen) {
        const t = setTimeout(() => setVisible(true), 400);
        return () => clearTimeout(t);
      }
    } catch {
      setVisible(true);
    }
  }, [storageKey]);

  useEffect(() => { if (forceOpen) setVisible(true); }, [forceOpen]);

  const handleClose = useCallback(() => {
    if (dontShowAgain) {
      try { localStorage.setItem(storageKey, new Date().toISOString()); } catch { /* ignore */ }
    }
    setVisible(false);
    if (onClose) onClose();
  }, [dontShowAgain, storageKey, onClose]);

  const steps = [
    { n: '01', title: 'Choose the batch', body: 'Pick the batch number this invoice belongs to. Batches already received by accounts are locked and cannot accept uploads.' },
    { n: '02', title: 'Select the category', body: 'Pick the invoice category. Each category lists the supporting documents it requires before a submission can go through.' },
    { n: '03', title: 'Find the purchase order', body: 'Search by PO number, supplier, or project code, then select the relevant line items for this invoice.' },
    { n: '04', title: 'Upload one PDF', body: 'Combine the invoice and every supporting document into a single PDF and upload it. Pages are classified and the data is read automatically.' },
    { n: '05', title: 'Review and submit', body: 'Check the extracted fields, correct anything that looks wrong, and submit. The invoice goes to accounts for review under the chosen batch.' },
  ];

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={760}
      centered
      destroyOnClose
      maskClosable={false}
      closable={false}
      styles={{
        body: { padding: 0 },
        content: { padding: 0, borderRadius: 6, overflow: 'hidden', boxShadow: '0 18px 48px rgba(0,0,0,0.18)' },
        mask: { background: 'rgba(0,0,0,0.45)' },
      }}
    >
      <div style={{ background: T.white, position: 'relative' }}>
        {/* Close × */}
        <button onClick={handleClose} aria-label="Close"
          style={{
            position: 'absolute', top: 16, right: 16, width: 30, height: 30, borderRadius: 4,
            background: T.white, border: `1px solid ${T.border}`, cursor: 'pointer',
            color: T.sub, fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
          }}>×</button>

        {/* Header */}
        <div style={{ padding: '32px 40px 20px', borderBottom: `1px solid ${T.line}` }}>
          <div style={{ fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: T.muted, fontWeight: 600, marginBottom: 12 }}>
            Procurement — Invoice Submission
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: T.ink, margin: '0 0 8px', letterSpacing: '-0.4px' }}>
            How to use this portal{username && username !== 'guest' ? `, ${username.split('@')[0]}` : ''}
          </h1>
          <p style={{ fontSize: 13.5, color: T.sub, lineHeight: 1.6, margin: 0, maxWidth: 560 }}>
            A short walkthrough of how to submit invoices. Reopen this any time from the “How to use” button in the header.
          </p>
        </div>

        {/* Steps */}
        <div style={{ padding: '24px 40px 8px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 36, rowGap: 18 }}>
            {steps.map((s) => (
              <div key={s.n}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.faint, fontFamily: "'Consolas','Monaco',monospace", minWidth: 20 }}>{s.n}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{s.title}</span>
                </div>
                <div style={{ fontSize: 12.5, color: T.sub, lineHeight: 1.55, paddingLeft: 32 }}>{s.body}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Important rule — rejected invoices */}
        <div style={{ margin: '20px 40px 4px', padding: '14px 16px', background: T.blueSoft, border: `1px solid ${T.blueLine}`, borderRadius: 4 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: T.blue, marginBottom: 6 }}>Rejected invoices</div>
          <p style={{ fontSize: 12.5, color: T.sub, lineHeight: 1.65, margin: 0 }}>
            If an invoice is rejected, re-upload the corrected document through the <strong style={{ color: T.ink }}>Revise</strong> option
            on that same submission (Log Sheet → Revise, or from the Dashboard). The revision stays in the
            <strong style={{ color: T.ink }}> original batch it was first submitted in</strong>. Do not re-submit a rejected
            invoice as a new entry in a different batch — that creates a duplicate and breaks the batch totals.
          </p>
        </div>

        {/* Footer */}
        <div style={{ padding: '18px 40px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Checkbox checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)}>
            <span style={{ fontSize: 12.5, color: T.sub }}>Don't show this again</span>
          </Checkbox>
          <Button type="primary" size="large" onClick={handleClose}
            style={{ background: T.blue, borderColor: T.blue, color: '#fff', height: 42, paddingInline: 24, borderRadius: 4, fontWeight: 600, fontSize: 13.5 }}>
            Got it, let's start
          </Button>
        </div>
      </div>
    </Modal>
  );
}